import { useState, useRef, useEffect } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Reveal from "@/components/Reveal";
import { sendContact } from "@/lib/api";
import useHeaderScroll from "@/lib/useHeaderScroll";
import LegalLinks from "@/components/LegalLinks";

const SPOTLIGHT = [
  "bg-[radial-gradient(circle_at_50%_30%,rgba(13,148,136,0.22),transparent_70%)]",
  "bg-[radial-gradient(circle_at_50%_30%,rgba(181,149,96,0.2),transparent_70%)]",
  "bg-[radial-gradient(circle_at_50%_30%,rgba(244,63,94,0.15),transparent_70%)]",
];

// Socialdeal uses the kept Socialpick logo (user decision); backgrounds must stay transparent so icons blend in
const APP_ICONS = ["/assets/socialpick-app.png", "/assets/bookitnow-app.png", "/assets/incontact-app.png"];
const APP_NAMES = ["Socialdeal", "BookItNow", "InContact"];

function AppIcon({ index, className }) {
  return <img src={APP_ICONS[index]} alt={APP_NAMES[index]} className={className} />;
}

function Orbit() {
  const refs = useRef([]);
  const boxRef = useRef(null);

  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const box = boxRef.current;
      if (box) {
        const w = box.offsetWidth;
        const t = (((now - t0) / 26000) * Math.PI * 2) % (Math.PI * 2);
        for (let i = 0; i < 3; i++) {
          const a = t + (i * 2 * Math.PI) / 3;
          const depth = Math.sin(a); // -1 = far back, +1 = close front
          const el = refs.current[i];
          if (!el) continue;
          // transform-only movement: no layout, no stutter
          el.style.transform = `translate(-50%,-50%) translate3d(${0.4 * w * Math.cos(a)}px, ${0.17 * w * depth}px, 0) scale(${0.7 + 0.3 * ((depth + 1) / 2)})`;
          el.style.opacity = `${0.5 + 0.5 * ((depth + 1) / 2)}`;
          el.style.zIndex = depth > 0 ? 2 : 1;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div ref={boxRef} className="relative w-72 h-72 xl:w-96 xl:h-96" data-testid="digital-orbit">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className="absolute left-1/2 top-1/2 will-change-transform"
        >
          <AppIcon index={i} className="w-20 h-20 xl:w-24 xl:h-24 object-cover rounded-[26%] shadow-[0_24px_50px_-14px_rgba(10,10,10,0.35)]" />
        </div>
      ))}
    </div>
  );
}

export default function DigitalServices() {
  const { t } = useLang();
  const d = t.digital;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await sendContact("digital", form.name, form.email, { Message: form.message });
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const inputCls = "w-full bg-transparent border border-[#0a0a0a]/25 text-[#0a0a0a] placeholder:text-[#0a0a0a]/40 focus:border-[#0d9488] px-4 py-3.5 font-body text-sm transition-colors";
  const navLinkCls = "hidden md:inline font-mono-data text-[0.62rem] tracking-[0.18em] text-[#0a0a0a]/55 hover:text-[#0d9488] transition-colors";
  const { scrolled, hidden } = useHeaderScroll();

  return (
    <main className="bg-white text-[#0a0a0a] overflow-x-hidden" data-testid="digital-page">
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[transform,background-color,border-color,backdrop-filter] duration-300 ease-out ${
          hidden ? "-translate-y-full" : "translate-y-0"
        } ${scrolled ? "bg-white/75 backdrop-blur-xl border-b border-[#0a0a0a]/10" : "bg-transparent border-b border-transparent"}`}
        data-testid="digital-header"
      >
        <div className="mx-auto max-w-[1400px] pl-6 pr-4 md:px-10 lg:px-16 py-4 flex items-center justify-between">
        <span data-testid="digital-home-link" className="flex items-center gap-3 group">
          <span className="relative block h-11 w-11">
            <img src="/assets/medallion.png" alt="Eagle Eyes medallion" className="h-11 w-11 rounded-full shadow-md transition-transform duration-500 group-hover:rotate-[10deg]" />
            <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-[#0d9488] to-[#f43f5e] font-digital font-bold text-white text-[0.42rem] shadow">EE</span>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-digital font-medium text-[0.78rem] tracking-[0.08em]">EAGLE EYES</span>
            <span className="font-mono-data text-[0.52rem] tracking-[0.3em] text-[#0d9488]">DIGITAL SERVICES</span>
          </span>
        </span>
        </div>
        <div className="absolute right-4 top-4 md:hidden"><LanguageSwitcher variant="light" /></div>
      </header>

      {/* Desktop: links + language button pinned to the top-right corner, same as the Real Estate landing */}
      <nav className="hidden md:flex fixed top-4 right-4 z-50 items-center gap-5">
        <a href="#apps" data-testid="digital-nav-portfolio" className={navLinkCls}>{d.navPortfolio.toUpperCase()}</a>
        <a href="#app-socialdeal" data-testid="digital-nav-deals" className={navLinkCls}>{d.navDeals.toUpperCase()}</a>
        <a href="#contact" data-testid="digital-nav-contact" className={navLinkCls}>{d.navContact.toUpperCase()}</a>
        <LanguageSwitcher variant="light" />
      </nav>

      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="digital-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_18%_20%,rgba(13,148,136,0.10),transparent_50%),radial-gradient(ellipse_at_85%_75%,rgba(244,63,94,0.08),transparent_50%)]" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.55] [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_78%)]"
          style={{ backgroundImage: "radial-gradient(rgba(10,10,10,0.09) 1px, transparent 1px)", backgroundSize: "26px 26px" }}
        />
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-28 pb-16 grid lg:grid-cols-2 gap-14 lg:gap-8 items-center">
          <Reveal>
            <p className="font-mono-data text-[0.66rem] tracking-[0.3em] text-[#0d9488] mb-7">{d.kicker}</p>
            <h1 className="font-gateway font-extrabold leading-[1.02] tracking-[-0.02em] text-[2.2rem] md:text-[3.8rem] max-w-[18ch]">{d.h1}</h1>
            <p className="mt-8 max-w-[52ch] font-body text-base md:text-lg leading-relaxed text-[#0a0a0a]/65">{d.intro}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#apps" data-testid="digital-hero-cta-portfolio" className="group inline-flex items-center gap-2 bg-[#0a0a0a] text-white font-mono-data text-[0.66rem] tracking-[0.18em] px-6 py-4 hover:bg-[#0d9488] transition-colors duration-300">
                {d.navPortfolio.toUpperCase()} <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a href="#contact" data-testid="digital-hero-cta-contact" className="inline-flex items-center gap-2 border border-[#0a0a0a]/25 font-mono-data text-[0.66rem] tracking-[0.18em] px-6 py-4 hover:border-[#0d9488] hover:text-[#0d9488] transition-colors duration-300">
                {d.navContact.toUpperCase()}
              </a>
            </div>
            <div className="mt-12 flex items-center gap-8" data-testid="digital-hero-stats">
              {[["03", d.statProducts], ["01", d.statLive], ["02", d.statLaunching]].map(([n, l], i) => (
                <span key={l} className="flex items-baseline gap-2">
                  <span className={`font-gateway font-extrabold text-[1.5rem] ${i === 1 ? "text-[#0d9488]" : i === 2 ? "text-[#f43f5e]" : "text-[#0a0a0a]"}`}>{n}</span>
                  <span className="font-mono-data text-[0.58rem] tracking-[0.22em] text-[#0a0a0a]/50">{l.toUpperCase()}</span>
                </span>
              ))}
            </div>
          </Reveal>
          <div className="hidden lg:flex lg:justify-end" data-testid="digital-hero-orbit">
            <div className="scale-[0.72] sm:scale-90 lg:scale-100 origin-center">
              <Orbit />
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-10 lg:px-16 py-16 md:py-20 border-t border-[#0a0a0a]/8 bg-[#fafafa]" data-testid="digital-capabilities">
        <Reveal>
          <p className="font-mono-data text-[0.66rem] tracking-[0.3em] text-[#0d9488] mb-10">{d.capsTitle}</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {d.caps.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06} className="contents">
              <div data-testid={`digital-cap-${i}`}>
                <h3 className="font-digital font-semibold text-[0.95rem] mb-2.5">{c.title}</h3>
                <p className="font-body text-[0.85rem] leading-relaxed text-[#0a0a0a]/60">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="apps" className="px-6 md:px-10 lg:px-16 py-20 md:py-28 border-t border-[#0a0a0a]/8 scroll-mt-16" data-testid="digital-apps">
        <Reveal>
          <p className="font-mono-data text-[0.66rem] tracking-[0.3em] text-[#0d9488] mb-14">{d.appsTitle}</p>
        </Reveal>
        <div className="space-y-16 md:space-y-24">
          {d.apps.map((app, i) => (
            <Reveal key={app.name} delay={0.05}>
              <article
                id={app.name === "Socialdeal" ? "app-socialdeal" : undefined}
                data-testid={`digital-app-${app.name.toLowerCase()}`}
                className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center scroll-mt-24 ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}
              >
                <div className={`md:col-span-5 [direction:ltr] relative flex items-center justify-center py-14 border border-[#0a0a0a]/10 overflow-hidden ${SPOTLIGHT[i]}`}>
                  <span className="absolute top-4 left-5 font-mono-data text-[0.6rem] tracking-[0.24em] text-[#0a0a0a]/35">{String(i + 1).padStart(2, "0")}</span>
                  <span className={`absolute top-4 right-5 font-mono-data text-[0.6rem] tracking-[0.2em] px-2.5 py-1 ${app.status === "live" ? "bg-[#0d9488] text-white" : "bg-[#0a0a0a] text-white"}`} data-testid={`digital-app-status-${app.name.toLowerCase()}`}>
                    {(app.status === "live" ? d.live : d.soon).toUpperCase()}
                  </span>
                  <AppIcon index={i} className="w-24 h-24 md:w-28 md:h-28 object-cover rounded-[26%] shadow-2xl hover:scale-105 hover:-rotate-2 transition-transform duration-500" />
                </div>
                <div className="md:col-span-7 [direction:ltr]">
                  <p className="font-mono-data text-[0.62rem] tracking-[0.26em] text-[#0d9488] mb-3">{app.tag.toUpperCase()}</p>
                  <h2 className="font-digital font-bold text-[1.9rem] md:text-[2.7rem] tracking-tight mb-4">{app.name}</h2>
                  <p className="font-body text-[0.98rem] md:text-[1.05rem] leading-relaxed text-[#0a0a0a]/65 max-w-[52ch] mb-6">{app.desc}</p>
                  <ul className="mb-7 space-y-2">
                    {app.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 font-body text-[0.88rem] text-[#0a0a0a]/70">
                        <Check className="h-3.5 w-3.5 text-[#0d9488] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {app.url ? (
                    <a
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`digital-app-cta-${app.name.toLowerCase()}`}
                      className="group inline-flex items-center gap-2 font-mono-data text-[0.66rem] tracking-[0.18em] border border-[#0a0a0a] px-6 py-3.5 hover:bg-[#0a0a0a] hover:text-white transition-colors"
                    >
                      {app.cta.toUpperCase()}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  ) : (
                    <span data-testid={`digital-app-cta-${app.name.toLowerCase()}`} className="inline-flex items-center gap-2 font-mono-data text-[0.66rem] tracking-[0.18em] border border-[#0a0a0a]/25 text-[#0a0a0a]/45 px-6 py-3.5">
                      {app.cta.toUpperCase()}
                    </span>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-10 lg:px-16 py-16 md:py-20 border-t border-[#0a0a0a]/8 bg-[#0a0a0a] text-white" data-testid="digital-studio">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
          <Reveal>
            <h2 className="font-digital font-bold text-[1.7rem] md:text-[2.3rem] tracking-tight mb-5">{d.studioTitle}</h2>
            <p className="font-body text-[0.98rem] leading-relaxed text-white/60 max-w-[52ch]">{d.studioText}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex gap-10 md:justify-end">
              {[
                { n: "03", l: d.statProducts },
                { n: "01", l: d.statLive },
                { n: "02", l: d.statLaunching },
              ].map((s) => (
                <div key={s.l} className="text-center" data-testid={`digital-stat-${s.l.toLowerCase()}`}>
                  <p className="font-digital font-bold text-[2.2rem] md:text-[2.8rem] leading-none bg-gradient-to-r from-[#14b8a6] to-[#f43f5e] bg-clip-text text-transparent">{s.n}</p>
                  <p className="font-mono-data text-[0.6rem] tracking-[0.24em] text-white/50 mt-2">{s.l.toUpperCase()}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="contact" className="px-6 md:px-10 lg:px-16 py-20 md:py-28 bg-[#fafafa] scroll-mt-16" data-testid="digital-contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-digital font-bold text-[1.8rem] md:text-[2.4rem] tracking-tight mb-4">{d.contactTitle}</h2>
            <p className="font-body text-[1rem] leading-relaxed text-[#0a0a0a]/65 max-w-[44ch]">{d.contactText}</p>
          </Reveal>
          <Reveal delay={0.1}>
            {status === "sent" ? (
              <p data-testid="digital-form-success" className="font-body text-sm text-[#0d9488] border border-[#0d9488]/40 bg-white px-5 py-6">{t.form.sent}</p>
            ) : (
              <form className="grid gap-3" onSubmit={submit} data-testid="digital-contact-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="digital-form-name" type="text" required placeholder={t.form.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                  <input data-testid="digital-form-email" type="email" required placeholder={t.form.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                </div>
                <textarea data-testid="digital-form-message" required rows={4} placeholder={d.messageField} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                <button data-testid="digital-form-submit" type="submit" disabled={status === "sending"} className="justify-self-start mt-1 bg-[#0a0a0a] text-white font-mono-data text-[0.66rem] tracking-[0.18em] px-6 py-4 hover:bg-[#0d9488] transition-colors disabled:opacity-50">
                  {status === "sending" ? t.form.sending.toUpperCase() : d.contactButton.toUpperCase()}
                </button>
                {status === "error" && <p data-testid="digital-form-error" className="text-[0.78rem] text-crimson">{t.form.error}</p>}
              </form>
            )}
          </Reveal>
        </div>
      </section>

      <footer className="px-6 md:px-10 lg:px-16 py-10 flex flex-wrap items-end justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-6" data-testid="digital-footer">
        <div>
          <p className="font-digital font-medium tracking-[0.06em] text-[0.9rem]">{d.footerBrand.toUpperCase()}</p>
          <p className="font-mono-data text-[0.6rem] tracking-[0.2em] text-[#0a0a0a]/45 mt-2">{t.common.company}</p>
        </div>
        <LegalLinks className="font-mono-data text-[0.6rem] tracking-[0.2em] text-[#0a0a0a]/45 order-last w-full text-center lg:order-none lg:w-auto" />
        <span className="lg:justify-self-end font-mono-data text-[0.6rem] tracking-[0.2em] text-[#0a0a0a]/45">© {new Date().getFullYear()} {d.footerBrand}</span>
      </footer>
    </main>
  );
}
